import { useMemo } from 'react';

import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';
import { hasItem, makeTab } from '@sravni/ugc/lib/specialOffers/helpers';
import {
  ACTIONS,
  BANK_DEFAULT_TABS_ORDER,
  COMMON_OFFERS,
  EDUCATION_DEFAULT_TABS_ORDER,
  INSURANCE_DEFAULT_TABS_ORDER,
  MFO_DEFAULT_TABS_ORDER,
} from '@sravni/ugc/lib/specialOffers/lists';
import { PRODUCT_TAG_TO_SPECIAL_OFFERS_TABS_MAP } from '@sravni/ugc/lib/specialOffers/productTagToTab';
import {
  checkIfBroker,
  checkIfBunkruptcy,
  checkIfInsurancePage,
  checkIfMfo,
  checkIfSchool,
} from '@sravni/ugc/lib/utils/checkOrganizationsType';

import { PRODUCT_TYPE } from '../constants/reviews';
import { getReviewType } from '../helpers/reviews';
import { useProductsTags, useServerFilters } from '../store/hooks/filters';
import { useIsUserLogged } from '../store/hooks/users';

interface OffersTabsParams {
  reviewTag: string;
  type: PAGE_TYPE | PRODUCT_TYPE;
}

export const useTabsForSpecialOffers = ({ reviewTag, type }: OffersTabsParams) => {
  const isUserLogged = useIsUserLogged();
  const isInsurancePages =
    checkIfInsurancePage(type as PAGE_TYPE) || ((type as unknown) as PRODUCT_TYPE) === PRODUCT_TYPE.OSAGO;
  const isSchool = checkIfSchool(type as PAGE_TYPE);
  const isMfo = checkIfMfo(type as PAGE_TYPE);
  const isBroker = checkIfBroker(type as PAGE_TYPE);
  const isBunkruptcy = checkIfBunkruptcy(type as PAGE_TYPE);

  const serverFilters = useServerFilters();
  const productsTags = useProductsTags();

  const reviewType = getReviewType(type, productsTags);
  const tagsForThisType = serverFilters[reviewType.typeForService]?.tag;

  const productTag = tagsForThisType?.find(
    ({ key, alias }) => [key, alias].includes(reviewTag) || [key, alias].includes(type),
  );
  const productAlias = productTag?.alias;

  const tabs = useMemo(() => {
    const tabList = [];

    if (productAlias) {
      const tab = PRODUCT_TAG_TO_SPECIAL_OFFERS_TABS_MAP[productAlias];

      if (tab) {
        tabList.push(...tab);
      }
    }

    if (isSchool) {
      tabList.push(...EDUCATION_DEFAULT_TABS_ORDER);
    } else if (isMfo) {
      tabList.push(...MFO_DEFAULT_TABS_ORDER);
    } else if (isInsurancePages) {
      if (isUserLogged) {
        return [makeTab(ACTIONS.osago)];
      }
      tabList.push(...INSURANCE_DEFAULT_TABS_ORDER);
    } else if (isBroker) {
      tabList.push(...[makeTab(COMMON_OFFERS.brokerskoeObsluzhivanie), ...BANK_DEFAULT_TABS_ORDER]);
    } else if (isBunkruptcy) {
      tabList.push(...[makeTab(COMMON_OFFERS.bankrotstvo), ...BANK_DEFAULT_TABS_ORDER]);
    } else {
      tabList.push(...BANK_DEFAULT_TABS_ORDER);
    }

    return tabList.reduce((acc, tab) => (hasItem(acc, tab) ? acc : [...acc, tab]), []);
  }, [isBroker, isInsurancePages, isMfo, isSchool, isUserLogged, productAlias, isBunkruptcy]);

  return tabs;
};
